import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  Package,
  Sparkles,
  Globe,
  BarChart3,
  Menu,
  X,
  ChevronRight,
  LogOut,
  User,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/admin/niches", icon: Layers, label: "Niches" },
  { path: "/admin/products", icon: Package, label: "Products" },
  { path: "/admin/content", icon: Sparkles, label: "AI Content" },
  { path: "/admin/scraper", icon: Globe, label: "Scraper" },
  { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { setAdminTheme } = useTheme();
  const { user, logout } = useAuth();

  useEffect(() => {
    setAdminTheme();
  }, [setAdminTheme]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#121212] border-r border-white/10 transition-all duration-300 z-50 ${
          sidebarOpen ? "w-64" : "w-16"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen && (
            <span className="font-mono text-lg font-bold tracking-wider text-[#007AFF]">
              AFFILIATE HUB
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-white/10 transition-colors"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                  active
                    ? "bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30"
                    : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={20} />
                {sidebarOpen && (
                  <>
                    <span className="font-medium">{item.label}</span>
                    {active && <ChevronRight size={16} className="ml-auto" />}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Public Site Link & Logout */}
        {sidebarOpen && (
          <div className="absolute bottom-4 left-4 right-4 space-y-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-white rounded-md border border-white/10 hover:border-white/20 transition-colors"
              data-testid="view-public-site-btn"
            >
              <Globe size={16} />
              View Public Site
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 rounded-md border border-red-500/20 hover:border-red-500/40 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        {/* Top Bar */}
        <header className="h-16 bg-[#121212] border-b border-white/10 flex items-center justify-between px-6">
          <div className="flex items-center gap-2 text-[#A1A1AA]">
            <span className="text-sm font-mono">Admin</span>
            <ChevronRight size={14} />
            <span className="text-sm text-white capitalize">
              {location.pathname.split("/").pop() || "Dashboard"}
            </span>
          </div>
          {user && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <User size={16} />
                <span className="text-sm">{user.name || user.email}</span>
              </div>
            </div>
          )}
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
